// This Source Code Form is subject to the terms of the Mozilla Public License, v. 2.0.
// If a copy of the MPL was not distributed with this file, You can obtain one at https://mozilla.org/MPL/2.0/.

import React, { useState, useEffect, useMemo } from "react"
import { DateTime } from "luxon"
import { useSelector, useDispatch } from "react-redux"
import styles from "./CurrentTimeUnitSlider.module.css"
import { useTranslation } from "react-i18next"
import { setSelectedTimeEntity, setCurrentData, setRedrawKey } from "../../../../components/SessionArea/sessionSlice"
import session from "../../../../components/SessionArea/sessionSlice.selectors"
import { FMSlider } from "../../../../components"
import api from "../../../../app/api"
import { v4 as uuidv4 } from "uuid"

const CurrentTimeUnitSlider = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const auth0AccessToken = useSelector(session.selectAuth0AccessToken)
    const currentAvailableTimeRange = useSelector(session.selectCurrentAvailableTimeRange)
    const selectedTimeRangeForCurrentData = useSelector(session.selectSelectedTimeRangeForCurrentData)
    const selectedTimeEntity = useSelector(session.selectSelectedTimeEntity)
    const currentCategory = useSelector(session.selectCurrentCategory)
    const currentIndicator = useSelector(session.selectCurrentIndicator)
    const currentSpatialResolution = useSelector(session.selectCurrentSpatialResolution)
    const currentTemporalResolution = useSelector(session.selectCurrentTemporalResolution)

    // local state
    const [previouslySelectedTimeRangeForCurrentData, setPreviouslySelectedTimeRangeForCurrentData] =
        useState(undefined)
    const [previouslySelectedTimeEntity, setPreviouslySelectedTimeEntity] = useState(undefined)
    const [previousTemporalResolution, setPreviousTemporalResolution] = useState(undefined)

    // hang on to previous values when category changes
    useEffect(() => {
        const savePreviousValues = async () => {
            if (!currentCategory) {
                return
            }

            // cache selected values temporarily
            // this helps determine whether we can keep the currently selected time unit or fall back to the default
            setPreviouslySelectedTimeRangeForCurrentData(selectedTimeRangeForCurrentData)
            setPreviouslySelectedTimeEntity(selectedTimeEntity)
            setPreviousTemporalResolution(currentTemporalResolution)
        }
        savePreviousValues()
    }, [currentCategory])

    // selected time range is changed in data parameters (also happens on indicator change!)
    useEffect(() => {
        if (currentAvailableTimeRange === undefined || selectedTimeRangeForCurrentData === undefined) {
            return
        }
        const adjustSelectedTimeEntity = async () => {
            // start with the default: first entity in the new time range
            let newSelectedTimeEntity = [selectedTimeRangeForCurrentData[0]]
            // re-use previously selected time entity if possible
            if (
                // only works if temp resolution is the same
                previousTemporalResolution === currentTemporalResolution &&
                // only if there is a cached value
                previouslySelectedTimeEntity !== undefined &&
                // only bother checking if the ranges valid...
                selectedTimeRangeForCurrentData !== undefined &&
                previouslySelectedTimeRangeForCurrentData !== undefined &&
                Array.isArray(selectedTimeRangeForCurrentData) &&
                Array.isArray(previouslySelectedTimeRangeForCurrentData) &&
                selectedTimeRangeForCurrentData?.length === 2 &&
                previouslySelectedTimeRangeForCurrentData?.length === 2 &&
                // ... and the same
                JSON.stringify(selectedTimeRangeForCurrentData) ===
                    JSON.stringify(previouslySelectedTimeRangeForCurrentData) &&
                // only if the cached value is within the current range
                previouslySelectedTimeEntity[0] >= selectedTimeRangeForCurrentData[0] &&
                previouslySelectedTimeEntity[0] <= selectedTimeRangeForCurrentData[1]
            ) {
                newSelectedTimeEntity = selectedTimeEntity
            }

            // update "previously selected items" cache
            setPreviouslySelectedTimeRangeForCurrentData(selectedTimeRangeForCurrentData)
            setPreviouslySelectedTimeEntity(selectedTimeEntity)
            setPreviousTemporalResolution(currentTemporalResolution)

            //console.debug("Available time range: ", availableTimeRangeForCurrentData)
            //console.debug("New selected time range: ", selectedTimeRangeForCurrentData, selectedTimeRangeForCurrentData.map(t => availableTimeRangeForCurrentData[t]))
            //console.debug("Selected time entity: ", selectedTimeEntity, availableTimeRangeForCurrentData[selectedTimeEntity])
            dispatch(setSelectedTimeEntity(newSelectedTimeEntity))
            console.debug(
                "Selected time range for current data changed. New selected time entity: ",
                newSelectedTimeEntity,
                currentAvailableTimeRange[newSelectedTimeEntity]
            )
        }
        adjustSelectedTimeEntity()
    }, [selectedTimeRangeForCurrentData])

    // selected time entity is changed, e.g. using slider
    useEffect(() => {
        if (selectedTimeEntity === undefined || currentAvailableTimeRange === undefined) {
            return
        }
        const loadData = async () => {
            // ensure the selected entity is within the available range
            const newSelectedTimeEntity =
                selectedTimeEntity < selectedTimeRangeForCurrentData[0]
                    ? selectedTimeRangeForCurrentData[0]
                    : selectedTimeEntity > selectedTimeRangeForCurrentData[1]
                    ? selectedTimeRangeForCurrentData[1]
                    : selectedTimeEntity
                    ? selectedTimeEntity
                    : selectedTimeRangeForCurrentData[0]
            //dispatch(setSelectedTimeEntity(newSelectedTimeEntity))

            console.debug(
                "Loading data for selected time entity: ",
                newSelectedTimeEntity,
                currentAvailableTimeRange[newSelectedTimeEntity]
            )
            const query_parameters = {
                category_id: currentIndicator.category_id,
                indicator_id: currentIndicator.indicator_id,
                srid: currentSpatialResolution.srid,
                trid: currentTemporalResolution.trid,
                start_date: currentAvailableTimeRange[newSelectedTimeEntity],
                duration: 1
            }
            let response = await api.query(auth0AccessToken, query_parameters)
            if (!response || !response.data_by_date) {
                dispatch(setCurrentData(undefined))
                dispatch(setRedrawKey(uuidv4()))
                return
            }
            dispatch(setCurrentData(response.data_by_date[currentAvailableTimeRange[newSelectedTimeEntity]]))
            dispatch(setRedrawKey(uuidv4()))
        }
        loadData()
    }, [currentIndicator, selectedTimeEntity])

    // labels are derived synchronously from the redux state so they stay in sync
    // when any of the inputs change (previously an effect with incomplete deps could
    // leave labels stale or unset if temporal resolution was momentarily undefined)
    const labels = useMemo(() => {
        if (!currentAvailableTimeRange) {
            return undefined
        }
        if (
            currentCategory?.type === "flow" &&
            currentTemporalResolution?.date_format &&
            currentTemporalResolution?.relativedelta_unit
        ) {
            const frm_str = currentTemporalResolution.date_format
                .replace("%Y", "yyyy")
                .replace("%m", "MM")
                .replace("%d", "dd")
            // TODO: This assumes that currentAvailableTimeRange is continuous - we should change
            // this to use the previous date in currentAvailableTimeRange
            return currentAvailableTimeRange.map(date_str => {
                const date = DateTime.fromFormat(date_str, frm_str)
                const lastdate = date.minus({ [currentTemporalResolution.relativedelta_unit]: 1 }).toFormat(frm_str)
                return lastdate + " to " + date.toFormat(frm_str)
            })
        }
        return currentAvailableTimeRange
    }, [currentAvailableTimeRange, currentCategory, currentTemporalResolution])

    // react-range's checkBoundaries throws on componentDidMount whenever
    // min >= max OR the value is out of [min, max]. PR #73's force-remount
    // means this throw is hit every time the time range changes, including
    // the degenerate cases below:
    //   - [N, N]   user collapsed the Menu's range slider to one entity
    //   - [M, N] with M > N   inverted range (e.g. DatePicker indexOf
    //                          returned -1 for one endpoint)
    //   - [-1, *] / [*, -1]   DatePicker couldn't find the date
    //   - non-finite values
    // A slider over a degenerate range is meaningless, so render a label
    // (or just nothing) and skip mounting the slider.
    const hasValidRange =
        Array.isArray(selectedTimeRangeForCurrentData) &&
        Number.isFinite(selectedTimeRangeForCurrentData[0]) &&
        Number.isFinite(selectedTimeRangeForCurrentData[1]) &&
        selectedTimeRangeForCurrentData[0] < selectedTimeRangeForCurrentData[1] &&
        selectedTimeRangeForCurrentData[0] >= 0
    const isSinglePoint =
        Array.isArray(selectedTimeRangeForCurrentData) &&
        selectedTimeRangeForCurrentData[0] === selectedTimeRangeForCurrentData[1] &&
        Number.isFinite(selectedTimeRangeForCurrentData[0]) &&
        selectedTimeRangeForCurrentData[0] >= 0
    const singleLabel =
        isSinglePoint && labels ? labels[selectedTimeRangeForCurrentData[0]] : undefined

    return (
        <div className={styles.CurrentTimeUnitSlider} data-testid="CurrentTimeUnitSlider">
            {currentAvailableTimeRange && hasValidRange && (
                <FMSlider
                    // force a fresh mount whenever min/max change. react-range
                    // 1.8.14's componentDidUpdate has a bug: when min/max/step
                    // change, it rebuilds markRefs with fresh createRef objects
                    // and calls calculateMarkOffsets in the same tick — before
                    // those new refs are attached to the DOM. That gives every
                    // mark a -4999px position via the markWidth=9999 fallback,
                    // and the offsets are never recalculated once the refs do
                    // attach. Key on the time range (which is what feeds min/max)
                    // so any min/max change triggers a remount instead of the
                    // broken update path — componentDidMount runs against
                    // attached refs and gets correct offsets.
                    key={`${selectedTimeRangeForCurrentData[0]}-${selectedTimeRangeForCurrentData[1]}`}
                    // clamp values to [min, max]. After a remount triggered by
                    // a new time range, selectedTimeEntity (from redux) is
                    // briefly the previous indicator's value, which can fall
                    // outside the new range. The effect on line 51 dispatches
                    // a corrected entity but only on the next render, and
                    // react-range's componentDidMount calls checkBoundaries
                    // synchronously and throws RangeError if value<min — which
                    // blanks the whole dashboard.
                    values={[
                        Math.min(
                            Math.max(selectedTimeEntity?.[0] ?? selectedTimeRangeForCurrentData[0], selectedTimeRangeForCurrentData[0]),
                            selectedTimeRangeForCurrentData[1]
                        )
                    ]}
                    outerLabelsOnly={true}
                    step={1}
                    min={selectedTimeRangeForCurrentData[0]}
                    max={selectedTimeRangeForCurrentData[1]}
                    onChange={values => {
                        setPreviouslySelectedTimeEntity(selectedTimeEntity)
                        dispatch(setSelectedTimeEntity(values))
                    }}
                    labels={labels}
                    isRange={false}
                    cumulative={false}
                    outputIsVisible={false}
                />
            )}
            {currentAvailableTimeRange && isSinglePoint && (
                <span className={styles.Info}>{singleLabel}</span>
            )}
            {(!currentAvailableTimeRange || !selectedTimeRangeForCurrentData) && (
                <span className={styles.Info}>{t("dashboard.slider_text")}</span>
            )}
        </div>
    )
}

export default CurrentTimeUnitSlider
